export type BulkBusinessGroupAssignmentUnitState = {
  unitKey: string;
  connectionKey: string;
  isSecondaryResource: boolean;
  isParentAssigned: boolean;
};

/**
 * A secondary resource is the assignable entity, but older assignment flows
 * also persisted its parent Connection. Once the draft contains no selected
 * resource for that Connection, the parent mapping is stale and must be
 * removed in the same batch.
 */
export const getParentConnectionKeysWithoutSelectedResources = (
  units: readonly BulkBusinessGroupAssignmentUnitState[],
  selectedUnitKeys: readonly string[],
): string[] => {
  const selected = new Set(selectedUnitKeys);
  const secondaryUnitsByConnection = new Map<
    string,
    BulkBusinessGroupAssignmentUnitState[]
  >();

  units.forEach((unit) => {
    if (!unit.isSecondaryResource || !unit.connectionKey) return;
    secondaryUnitsByConnection.set(unit.connectionKey, [
      ...(secondaryUnitsByConnection.get(unit.connectionKey) || []),
      unit,
    ]);
  });

  return [...secondaryUnitsByConnection.entries()].flatMap(
    ([connectionKey, connectionUnits]) =>
      connectionUnits.some((unit) => unit.isParentAssigned) &&
      !connectionUnits.some((unit) => selected.has(unit.unitKey))
        ? [connectionKey]
        : [],
  );
};

/**
 * Keep persisted selections visible at the top of a long assignment list.
 * The order depends on server state rather than the mutable draft so rows do
 * not jump away when the user checks or unchecks them.
 */
export const prioritizePersistedAssignedUnits = <T extends { key: string }>(
  units: readonly T[],
  persistedAssignedUnitKeys: ReadonlySet<string>,
): T[] =>
  units
    .map((unit, index) => ({
      unit,
      index,
      assigned: persistedAssignedUnitKeys.has(unit.key),
    }))
    .sort((left, right) =>
      Number(right.assigned) - Number(left.assigned) || left.index - right.index,
    )
    .map(({ unit }) => unit);
